import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PrivacyPolicyPopup from "../../components/PrivacyPolicyPopup";
import { submitContactForm, ContactFormData } from "../../apis/contact";
import { SEO } from "../../components/SEO";
import {
  Container,
  LeftSection,
  RightSection,
  ContentWrapper,
  LeftFrame,
  ContactUsHeading,
  TitleSection,
  SubTitle,
  Description,
  ButtonGroup,
  Button,
  ButtonText,
  InfoSection,
  MapIcon,
  ContactInfo,
  AddressText,
  EmailText,
  RightContentTitle,
  FormLabel,
  LabelText,
  RequiredMark,
  FormSection,
  ChipsContainer,
  Chip,
  ChipText,
  InputRowContainer,
  InputColumn,
  TextField,
  UnitText,
  DropdownField,
  ArrowIconWrapper,
  TextAreaContainer,
  TextareaFrame,
  TextAreaInput,
  FileAddSection,
  FileButton,
  FileButtonText,
  CaptionContainer,
  IconWrapper,
  CaptionText,
  TextAreaSection,
  ClientInfoSection,
  ClientInfoTitle,
  ClientInfoRow,
  AgreementSection,
  CheckboxContainer,
  Checkbox,
  AgreementText,
  HighlightText,
  NormalText,
  SubmitButtonContainer,
  SubmitButton,
  AgreementRow,
  InputField,
  AddedFileContainer,
  FileIcon,
  FileTextContent,
  FileName,
  FileSize,
  DeleteIconWrapper,
  MobileButtonGroup,
  MobileButton,
  MobileButtonRow,
  MobileInquiryButton,
  CustomBudgetDropdown,
  CustomBudgetOption,
  LoadingSpinner,
  ButtonContentWrapper,
  MobileButtonContainer,
  ErrorModalOverlay,
  ErrorModalContent,
  ErrorIcon,
  ErrorMessage,
  ErrorModalButton
} from "./styles";
import styled, { keyframes } from 'styled-components';
import { useFooter } from "../../contexts/FooterContext";

// Animation keyframe for slide up effect
const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// 좌측 영역 최대 스크롤 높이를 화면 높이에 따라 동적으로 계산
const getMaxScroll = () => Math.min(1000, window.innerHeight * 0.7);

// Animated content wrapper
const AnimatedContentWrapper = styled(ContentWrapper)`
  animation: ${slideUp} 0.8s ease-out forwards;
`;

// Animated left and right sections
const AnimatedLeftSection = styled(LeftSection)`
  animation: ${slideUp} 0.8s ease-out forwards;
`;

// Animated content for scrolling - we'll use window.scrollY directly
const ScrollingLeftContent = styled(ContentWrapper)`
  position: relative;
  animation: ${slideUp} 0.8s ease-out forwards;
`;

const AnimatedRightSection = styled(RightSection)`
  animation: ${slideUp} 0.8s ease-out forwards;
  animation-delay: 0.2s;
  opacity: 0;
  animation-fill-mode: forwards;
`;

// Bottom Sheet Modal for Mobile
const BottomSheetOverlay = styled.div<{ show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: ${props => props.show ? 'block' : 'none'};
`;

const BottomSheetContainer = styled.div<{ show: boolean, animate: boolean }>`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: auto;
  background-color: white;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  padding: 20px;
  z-index: 1001;
  transform: ${props => props.show ? 'translateY(0)' : 'translateY(100%)'};
  transition: ${props => props.animate ? 'transform 0.3s ease' : 'none'};
`;

const AgreementItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const ConfirmButton = styled.button`
  width: 100%;
  height: 60px;
  background-color: #FF4F00;
  color: white;
  font-size: 16px;
  font-family: 'Pretendard', sans-serif;
  font-weight: 500;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  margin-top: 20px;
  
  &:disabled {
    background-color: #DCDCDC;
    cursor: not-allowed;
  }
`;

const ArrowRight = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PrivacyContentHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 50px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const PrivacyTitle = styled.h3`
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  font-family: 'Pretendard', sans-serif;
  line-height: 28px;
  letter-spacing: -1%;
  vertical-align: middle;
`;

const PrivacyText = styled.div`
  font-size: 16px;
  line-height: 26px;
  color: #1D1D1D;
  font-family: 'Pretendard', sans-serif;
  
  h2 {
    font-family: 'Pretendard', sans-serif;
    font-weight: 700;
    font-size: 24px;
    line-height: 36px;
    letter-spacing: -1%;
    margin-bottom: 40px;
    margin-top: 0;
  }
  
  p {
    font-size: 16px;
    line-height: 26px;
    margin-bottom: 30px;
  }
  
  strong {
    font-weight: 700;
    display: block;
    margin-top: 30px;
    margin-bottom: 0;
  }
  
  strong + p {
    margin-top: 0;
  }
`;

// Adding a new full-page component for privacy policy
const PrivacyFullPage = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: white;
  z-index: 1010;
  padding: 20px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

// Add ErrorModal component before the Contact component
interface ErrorModalProps {
  message: string;
  onClose: () => void;
}

const ErrorModal = ({ message, onClose }: ErrorModalProps) => {
  return (
    <ErrorModalOverlay onClick={onClose}>
      <ErrorModalContent onClick={e => e.stopPropagation()}>
        <ErrorIcon>
          <img src="/images/icon/error.svg" alt="Error" />
        </ErrorIcon>
        <ErrorMessage>{message}</ErrorMessage>
        <ErrorModalButton onClick={onClose}>확인</ErrorModalButton>
      </ErrorModalContent>
    </ErrorModalOverlay>
  );
};

const Contact = () => {
  const navigate = useNavigate();
  const { setShowFooter } = useFooter();
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 900);
  const [mobileStep, setMobileStep] = useState<number>(0);
  const leftContentRef = useRef<HTMLDivElement>(null);
  const [maxScroll, setMaxScroll] = useState(getMaxScroll());
  
  // Add resize handler for maxScroll
  useEffect(() => {
    const handleResize = () => {
      setMaxScroll(getMaxScroll());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // 페이지 로드 시 스크롤 위치를 최상단으로 설정 (모든 환경에서 작동하도록 강화)
  useEffect(() => {
    // 다양한 방법으로 스크롤 초기화 시도 (모바일 브라우저 대응)
    // 1. 즉시 실행
    window.scrollTo(0, 0);
    
    // 2. 지연 실행 (일부 모바일 브라우저는 약간의 지연 후에 더 안정적으로 작동)
    const timer1 = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 50);
    
    // 3. 약간 더 긴 지연으로 다시 시도
    const timer2 = setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant'
      });
      
      // 4. 강제로 body 요소에도 스크롤 적용
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }, 100);
    
    // 정리 함수에서 타이머 제거
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);
  
  // 모바일에서 스텝 변경 시에도 스크롤 위치를 최상단으로 설정
  useEffect(() => {
    if (isMobile) {
      // 다양한 방법으로 스크롤 초기화 시도
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }
  }, [mobileStep, isMobile]);
  
  // Add subtle parallax effect for the left content
  useEffect(() => {
    if (isMobile) return;
    
    let rafId: number | null = null;
    
    const handleScroll = () => {
      // 스크롤 이벤트가 발생할 때마다 애니메이션 프레임 요청
      if (rafId) return;
      
      rafId = requestAnimationFrame(() => {
        if (leftContentRef.current) {
          const scrollY = window.scrollY;
          const scrollFactor = 0.95;
          
          // maxScroll 상태 사용
          let offset = Math.min(scrollY * scrollFactor, maxScroll);
          
          // transform 직접 적용
          leftContentRef.current.style.transform = `translateY(${offset}px)`;
        }
        rafId = null;
      });
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isMobile, maxScroll]);
  
  // Update Footer visibility based on mobile step
  useEffect(() => {
    if (isMobile) {
      // Only show footer on the first mobile screen (step 0)
      setShowFooter(mobileStep === 0);
    } else {
      // Always show footer on desktop
      setShowFooter(true);
    }
    
    return () => {
      // Reset footer visibility when component unmounts
      setShowFooter(true);
    };
  }, [isMobile, mobileStep, setShowFooter]);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // 문의 종류 옵션 배열 - 한글명만 사용
  const inquiryTypes = [
    "퍼포먼스 마케팅",
    "그로스 컨설팅",
    "UGC 마케팅",
    "IMC 통합 마케팅",
    "콘텐츠 제작",
    "온드미디어",
    "기타"
  ];

  // 모바일에서 표시할 문의 종류 순서 - 가로 정렬용
  const mobileInquiryOrder = [
    "퍼포먼스 마케팅",
    "UGC 마케팅", 
    "콘텐츠 제작",
    "그로스 컨설팅",
    "IMC 통합 마케팅",
    "온드미디어",
    "기타"
  ];

  // 선택된 문의 종류 상태를 단일 값에서 배열로 변경
  const [selectedInquiries, setSelectedInquiries] = useState<string[]>(["퍼포먼스 마케팅"]);

  // 개인정보 수집 및 이용 동의 상태
  const [isAgreed, setIsAgreed] = useState(false);
  
  // 개인정보 수집 및 이용 팝업 상태
  const [isPrivacyPolicyOpen, setIsPrivacyPolicyOpen] = useState(false);
  
  // 뉴스레터 구독 동의 상태
  const [isNewsletterAgreed, setIsNewsletterAgreed] = useState(false);

  // 새로운 상태 추가
  const [duration, setDuration] = useState<string>('');
  const [budget, setBudget] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');

  // 파일 상태 추가
  const [files, setFiles] = useState<File[]>([]);

  // 이메일 유효성 검사
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
  
  // 문의 종류 선택 핸들러 수정 - 중복 선택 가능하도록
  const handleInquirySelect = (inquiry: string) => {
    // 이미 선택된 상태라면 제거, 아니면 추가
    if (selectedInquiries.includes(inquiry)) {
      setSelectedInquiries(selectedInquiries.filter(item => item !== inquiry));
    } else {
      setSelectedInquiries([...selectedInquiries, inquiry]);
    }
  };

  // 동의 상태 토글 핸들러
  const handleAgreementToggle = () => {
    setIsAgreed(!isAgreed);
  };

  // 뉴스레터 구독 동의 토글 핸들러
  const handleNewsletterToggle = () => {
    setIsNewsletterAgreed(!isNewsletterAgreed);
  };

  // 개인정보 수집 및 이용 안내 모달 표시 핸들러
  const handleShowAgreementDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPrivacyPolicyOpen(true);
  };

  // 개인정보 수집 및 이용 동의 확인 핸들러
  const handlePrivacyPolicyConfirm = () => {
    setIsAgreed(true);
    setIsPrivacyPolicyOpen(false);
  };

  // 지도 보기 버튼 클릭 핸들러
  const handleMapViewClick = () => {
    window.open("https://maps.app.goo.gl/sYXYzS8JmTC2ESVJA", "_blank");
  };

  // 회사소개서 다운로드 핸들러
  const handleDownloadClick = () => {
    // 회사 소개서 다운로드 로직
    try {
      const fileUrl = '/document/puzl.pdf';
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = 'PUZL_회사소개서.pdf';
      link.target = '_blank'; // Add target blank for better handling
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('다운로드 오류:', error);
      // 오류 발생 시 새 창에서 열기 시도
      window.open('/document/puzl.pdf', '_blank');
    }
  };

  // 기간 입력 핸들러 수정 - 숫자만 입력 가능하도록 변경
  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // 숫자만 추출
    const numbersOnly = value.replace(/[^0-9]/g, '');
    
    // 20자 이하로만 입력 가능하도록 제한
    if (numbersOnly.length <= 20) {
      setDuration(numbersOnly);
    }
  };

  // 문의 내용 입력 핸들러 - 1000자 제한
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= 1000) {
      setDescription(value);
    }
  };

  // 회사명, 담당자 이름 입력 핸들러 - 26자 제한
  const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 26) {
      setCompanyName(value);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 26) {
      setName(value);
    }
  };

  // 이메일 입력 핸들러 - 수정
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value); // 제한 삭제
    
    // 이메일 형식 검사 (NN@NN.NN)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsEmailValid(emailRegex.test(value));
  };

  // 연락처 입력 핸들러 - 수정
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // 숫자만 추출
    const numbersOnly = e.target.value.replace(/[^0-9]/g, '');
    
    // 11자 이하로 제한
    if (numbersOnly.length <= 11) {
      // 하이픈 추가 포맷팅
      let formattedPhone = '';
      if (numbersOnly.length > 7) {
        // 010-1234-5678 형식
        formattedPhone = `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(3, 7)}-${numbersOnly.slice(7)}`;
      } else if (numbersOnly.length > 3) {
        // 010-1234 형식
        formattedPhone = `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(3)}`;
      } else {
        // 그대로 표시
        formattedPhone = numbersOnly;
      }
      
      setPhone(formattedPhone);
    }
  };

  // 연락처 유효성 검사
  const isPhoneValid = phone.replace(/[^0-9]/g, '').length === 11;

  // 파일 첨부 핸들러 수정
  const handleFileUpload = () => {
    // 파일이 이미 2개라면 더 이상 추가하지 않음
    if (files.length >= 2) return;
    
    // input 요소 생성
    const input = document.createElement('input');
    input.type = 'file';
    
    // 허용된 파일 확장자 지정
    const allowedExtensions = ['.csv', '.doc', '.docx', '.jpg', '.pdf', '.png', '.ppt', '.xls'];
    input.accept = allowedExtensions.join(',');
    
    input.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        
        // 파일 확장자 추출
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        
        // 허용된 확장자인지 확인
        if (allowedExtensions.includes(fileExtension)) {
          // 기존 파일 배열에 새 파일 추가 (최대 2개까지)
          const newFiles = [...files, file];
          if (newFiles.length <= 2) {
            setFiles(newFiles);
          }
        } else {
          // 지원하지 않는 파일 형식 알림
          alert('지원하지 않는 파일 형식입니다. CSV, DOC, DOCX, JPG, PDF, PNG, PPT, XLS 형식의 파일만 업로드 가능합니다.');
        }
      }
    };
    
    // 파일 선택 다이얼로그 열기
    input.click();
  };

  // 파일 삭제 핸들러
  const handleFileRemove = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  // 파일 크기를 MB 단위로 변환하는 함수
  const formatFileSize = (size: number): string => {
    const fileSizeInMB = size / (1024 * 1024);
    return fileSizeInMB.toFixed(2) + ' MB';
  };

  // 모든 필수 입력 필드가 유효한지 확인
  const isFormValid = isEmailValid && isPhoneValid && 
                     companyName.trim() !== '' && 
                     name.trim() !== '' && 
                     duration.trim() !== '' &&
                     budget !== '' &&
                     description.trim() !== '' &&
                     selectedInquiries.length > 0 &&
                     (isMobile || isAgreed);

  // 모바일에서 문의하기 버튼 클릭 핸들러 수정
  const handleMobileInquiryClick = () => {
    // 새 페이지로 이동
    navigate('/contact/form');
    window.scrollTo(0, 0);
  };

  // Add loading state
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Modify validation functions to return first error only
  const validateForm = (formData: {
    selectedInquiries: string[],
    duration: string,
    budget: string,
    description: string,
    companyName: string,
    name: string,
    email: string,
    phone: string,
    isAgreed?: boolean, // 추가
  }): string | null => {
    if (formData.selectedInquiries.length === 0) {
      return "문의 종류를 선택해 주세요.";
    }
    
    if (!formData.duration.trim()) {
      return "프로젝트 예상 진행기간을 입력해 주세요.";
    }
    
    if (!formData.budget) {
      return "예산을 선택해 주세요.";
    }
    
    if (!formData.description.trim()) {
      return "문의 내용을 입력해 주세요.";
    }
    
    if (!formData.companyName.trim()) {
      return "회사명을 입력해 주세요.";
    }
    
    if (!formData.name.trim()) {
      return "담당자 이름을 입력해 주세요.";
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return "올바른 이메일 주소를 입력해 주세요.";
    }
    
    if (formData.phone.replace(/[^0-9]/g, '').length !== 11) {
      return "올바른 연락처를 입력해 주세요.";
    }
    
    // 개인정보 수집 동의 체크 (PC)
    if (typeof formData.isAgreed !== 'undefined' && !formData.isAgreed) {
      return "개인정보 수집 및 이용에 동의해 주세요.";
    }
    
    return null;
  };

  const validateFirstStep = (formData: {
    selectedInquiries: string[],
    duration: string,
    budget: string,
    description: string,
  }): string | null => {
    if (formData.selectedInquiries.length === 0) {
      return "문의 종류를 선택해 주세요.";
    }
    
    if (!formData.duration.trim()) {
      return "프로젝트 예상 진행기간을 입력해 주세요.";
    }
    
    if (!formData.budget) {
      return "예산을 선택해 주세요.";
    }
    
    if (!formData.description.trim()) {
      return "문의 내용을 입력해 주세요.";
    }
    
    return null;
  };

  // Modify submit handlers to use alert
  const handleSubmit = async () => {
    const error = validateForm({
      selectedInquiries,
      duration,
      budget,
      description,
      companyName,
      name,
      email,
      phone,
      isAgreed,
    });

    if (error) {
      alert(error);
      return;
    }

    try {
      setIsLoading(true);
      const inquiryTypesString = selectedInquiries.join(', ');
      
      const formData: ContactFormData = {
        inquiryType: inquiryTypesString,
        duration: Number(duration),
        budget: budget,
        content: description,
        companyName,
        contactPerson: name,
        email,
        phoneNumber: phone,
        isNewsLetterAgreed: isNewsletterAgreed,
        file1: files[0] || null,
        file2: files[1] || null,
      };

      await submitContactForm(formData);
      navigate('/contact/result');
      window.scrollTo(0, 0);
    } catch (error) {
      alert('문의 제출 중 오류가 발생했습니다. 다시 시도해 주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  // 모바일 다단계 폼 상태
  const [showMobileAgreement, setShowMobileAgreement] = useState(false);
  
  // State for bottom sheet
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [bottomSheetPrivacyAgreed, setBottomSheetPrivacyAgreed] = useState(false);
  const [bottomSheetNewsletterAgreed, setBottomSheetNewsletterAgreed] = useState(false);

  // Add a state for showing privacy policy page in bottom sheet
  const [showPrivacyContent, setShowPrivacyContent] = useState(false);
  
  // Create a state to control whether the bottom sheet should animate
  const [animateBottomSheet, setAnimateBottomSheet] = useState(true);
  
  // Modified mobile submit handler to show bottom sheet
  const handleMobileSubmit = () => {
    const error = validateForm({
      selectedInquiries,
      duration,
      budget,
      description,
      companyName,
      name,
      email,
      phone,
    });

    if (error) {
      alert(error);
      return;
    }

    setBottomSheetPrivacyAgreed(false);
    setBottomSheetNewsletterAgreed(false);
    setShowBottomSheet(true);
    setShowPrivacyContent(false);
  };
  
  // Mobile agreement popup confirm handler (for the traditional popup)
  const handleMobileAgreementConfirm = async () => {
    setShowMobileAgreement(false);
    // Proceed with form submission
    try {
      setIsLoading(true);
      // 선택된 모든 문의 종류를 쉼표로 구분된 문자열로 변환
      const inquiryTypesString = selectedInquiries.join(', ');
      
      const formData: ContactFormData = {
        inquiryType: inquiryTypesString, // 한글 문의 유형 그대로 전송
        duration: Number(duration),
        budget: budget, // Send budget as string
        content: description,
        companyName,
        contactPerson: name,
        email,
        phoneNumber: phone,
        isNewsLetterAgreed: isNewsletterAgreed,
        file1: files[0] || null, // Add first file if exists
        file2: files[1] || null, // Add second file if exists
      };
      await submitContactForm(formData);
      // Navigate to result page instead of showing mobile complete
      navigate('/contact/result');
      window.scrollTo(0, 0);
    } catch (error) {
      alert('문의 제출 중 오류가 발생했습니다. 다시 시도해 주세요.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle bottom sheet confirm action
  const handleBottomSheetConfirm = async () => {
    if (!bottomSheetPrivacyAgreed) {
      alert('개인정보 수집 및 이용에 동의해 주세요.');
      return;
    }
    
    setShowBottomSheet(false);
    // Update the main agreement states
    setIsAgreed(bottomSheetPrivacyAgreed);
    setIsNewsletterAgreed(bottomSheetNewsletterAgreed);
    
    // Proceed with form submission
    try {
      setIsLoading(true);
      // 선택된 모든 문의 종류를 쉼표로 구분된 문자열로 변환
      const inquiryTypesString = selectedInquiries.join(', ');
      
      const formData: ContactFormData = {
        inquiryType: inquiryTypesString, // 한글 문의 유형 그대로 전송
        duration: Number(duration),
        budget: budget, // Send budget as string
        content: description,
        companyName,
        contactPerson: name,
        email,
        phoneNumber: phone,
        isNewsLetterAgreed: bottomSheetNewsletterAgreed,
        file1: files[0] || null, // Add first file if exists
        file2: files[1] || null, // Add second file if exists
      };
      await submitContactForm(formData);
      // Navigate to result page instead of showing mobile complete
      navigate('/contact/result');
      window.scrollTo(0, 0);
    } catch (error) {
      alert('문의 제출 중 오류가 발생했습니다. 다시 시도해 주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle opening the privacy policy page in full screen
  const handleOpenPrivacyPolicy = () => {
    setShowBottomSheet(false); // Hide bottom sheet first
    // Small timeout to allow bottom sheet animation to complete
    setTimeout(() => {
      setShowPrivacyContent(true); // Show privacy content
    }, 300);
  };

  // Handle returning from privacy policy to bottom sheet
  const handlePrivacyPolicyBack = () => {
    setAnimateBottomSheet(false); // Disable animation
    setShowPrivacyContent(false); // Hide privacy content first
    setShowBottomSheet(true); // Show bottom sheet immediately without animation
    
    // Re-enable animations after a short delay
    setTimeout(() => {
      setAnimateBottomSheet(true);
    }, 500);
  };

  // Component for the privacy policy page
  const PrivacyPolicyPage = ({ onBack }: { onBack: () => void }) => {
    return (
      <PrivacyFullPage>
        <PrivacyContentHeader>
          <BackButton onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="#1D1D1D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </BackButton>
          <PrivacyTitle>개인정보 수집 및 이용</PrivacyTitle>
          <div style={{ width: 24 }}></div>
        </PrivacyContentHeader>
        <PrivacyText>
          <h2>개인정보 수집 및 이용</h2>
          
          <p>(주)퍼즐코퍼레이션은 제휴 문의 사항 등록 시, 최소 범위에서 아래와 같이 개인정보를 수집・이용합니다.</p>
          
          <strong>1. 수집하는 개인정보 항목</strong>
          <p>소속, 이름, 전자우편 주소</p>
          
          <strong>2. 수집 및 이용 목적</strong>
          <p>제휴•문의 사항 접수 및 처리결과 회신</p>
          
          <strong>3. 개인정보의 이용 기간</strong>
          <p>목적 달성 후, 해당 개인정보를 바로 파기</p>
          
          <p>위 개인정보 수집•이용에 대한 동의를 거부할 권리가 있습니다. 다만 동의를 거부하는 경우 제휴•문의 사항 등록이 제한될 수 있습니다.</p>
        </PrivacyText>
      </PrivacyFullPage>
    );
  };

  // Contact 컴포넌트 내부에 추가
  const [budgetDropdownOpen, setBudgetDropdownOpen] = useState(false);
  const budgetOptions = [
    "1천 이하", "1천~5천", "5천~1억", "1억~3억", "3억~5억", "5억 이상"
  ];
  const budgetDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!budgetDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        budgetDropdownRef.current &&
        !budgetDropdownRef.current.contains(e.target as Node)
      ) {
        setBudgetDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [budgetDropdownOpen]);

  // 모바일 상단 네비게이션 바 (아이폰 스타일)
  const MobileTopNav = ({ onBack }: { onBack: () => void }) => (
    <div style={{
      width: '100%',
      height: '62px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      position: 'fixed',
      top: 0,
      left: 0,
      background: 'white',
      zIndex: 1000,
      borderBottom: '1px solid #F0F0F0',
    }}>
      <button
        onClick={onBack}
        style={{
          width: 56,
          height: 56,
          background: 'none',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          padding: 0,
        }}
        aria-label="뒤로가기"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18L9 12L15 6" stroke="#1D1D1D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <span style={{ flex: 1, textAlign: 'center', fontWeight: 500, fontSize: 16, color: '#1D1D1D', fontFamily: 'Pretendard, sans-serif' }}>
        Contact us
      </span>
      <div style={{ width: 56 }} />
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '2px',
        background: '#F0F0F0',
      }}>
        <div style={{
          height: '100%',
          width: mobileStep === 1 ? '50%' : '100%',
          background: '#FF4F00',
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );

  // Add validation to mobile step navigation
  const handleNextStep = () => {
    const error = validateFirstStep({
      selectedInquiries,
      duration,
      budget,
      description
    });

    if (error) {
      alert(error);
      return;
    }

    setMobileStep(2);
    window.scrollTo(0, 0);
  };

  return (
    <Container>
      <SEO 
        title="퍼즐코퍼레이션 | Contact - 프로젝트 문의하기"
        description="프로젝트 문의, 협업 및 견적 상담을 위한 접점. 퍼즐코퍼레이션과 함께 더 큰 성장을 이루고 싶다면 언제든지 연락해 주세요."
        keywords="퍼즐코퍼레이션, PUZL, 프로젝트문의, 견적, 파트너십, 협업, 디지털에이전시, 문의"
        ogTitle="퍼즐코퍼레이션 | Contact - 프로젝트 문의하기"
        ogDescription="프로젝트 문의, 협업 및 견적 상담을 위한 접점. 퍼즐코퍼레이션과 함께 더 큰 성장을 이루고 싶다면 언제든지 연락해 주세요."
        ogImage="/images/og-image2.png"
      />
      {isMobile ? (
        <>
          {/* 모바일 GNB(헤더) - 문의 초기 페이지에서만 노출, 실제 GNB 컴포넌트가 있다면 아래 주석을 교체하세요 */}
          {mobileStep === 0 && (
            // <GNB />
            <></> // GNB 컴포넌트가 있다면 여기에 추가
          )}

          {/* 모바일 상단 네비게이션 바 - 문의하기 버튼 이후 단계에서만 노출 */}
          {mobileStep > 0 && (
            <MobileTopNav onBack={() => {
              if (mobileStep === 0) {
                navigate(-1);
              } else {
                setMobileStep(mobileStep - 1);
                window.scrollTo(0, 0);
              }
            }} />
          )}
          {/* 상단바 공간 확보 - 문의하기 버튼 이후 단계에서만 */}
          {mobileStep > 0 && <div style={{ height: 40 }} />}
          
          {mobileStep === 0 ? (
            <AnimatedContentWrapper style={{ width: '100%', maxWidth: '710px', margin: '0 auto', textAlign: 'left', paddingTop: 100, height: 'auto', minHeight: 'unset' }}>
              <ContactUsHeading style={{ textAlign: 'left', marginBottom: 26 }}>Contact us</ContactUsHeading>
              <TitleSection style={{ alignItems: 'flex-start', textAlign: 'left' }}>
                <SubTitle style={{ textAlign: 'left' }}>
                  킥오프 미팅만 하더라도 다양한<br />
                  인사이트를 얻을 수 있습니다.
                </SubTitle>
                <Description style={{ textAlign: 'left' }}>
                  PUZL은 단발성이 아닌 장기적으로 함께 성장할 수 있는 파트너를 찾습니다. 프로젝트 의뢰서(RFP)나 파트너 문의를 주시면 최대한 빠르게 연락드리겠습니다.
                </Description>
              </TitleSection>
              {/* 회사 위치 및 문의하기 버튼 (모바일용) */}
              <div style={{ marginTop: 86 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
                  <MapIcon style={{ marginBottom: 12 }}>
                    <img src="/images/icon/map.svg" alt="Map" width="36" height="36" />
                  </MapIcon>
                  <ContactInfo style={{ alignItems: 'flex-start', textAlign: 'left', gap: 0 }}>
                    <AddressText style={{ textAlign: 'left', marginBottom: 2 }}>서울 마포구 양화로 81, L104</AddressText>
                    <EmailText style={{ textAlign: 'left', marginBottom: 20 }}>contact@puzl.co.kr</EmailText>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: 20, marginTop: 0 }}>
                      <Button onClick={handleDownloadClick}>
                        <ButtonText>회사소개서 다운로드</ButtonText>
                      </Button>
                      <Button onClick={handleMapViewClick}>
                        <ButtonText>지도보기</ButtonText>
                      </Button>
                    </div>
                  </ContactInfo>
                </div>
                <SubmitButton style={{ margin: '30px 0 60px 0', width: 335, height: 60, fontSize: 18, background: '#FF4F00' }} onClick={() => {setMobileStep(1); window.scrollTo(0, 0);}}>
                  문의하기
                </SubmitButton>
              </div>
            </AnimatedContentWrapper>
          ) : mobileStep === 1 ? (
            <ContentWrapper style={{ width: '100%', maxWidth: '710px', margin: '0 auto', paddingTop: 0 }}>
              <RightContentTitle style={{ marginTop: 48, marginBottom: 40 }}>문의하고 싶은 내용을 알려주세요.</RightContentTitle>
              {/* 문의하고 싶은 내용 입력 영역만 */}
              <FormSection>
                <FormLabel>
                  <LabelText>문의 종류</LabelText>
                  <RequiredMark>*</RequiredMark>
                </FormLabel>
                <ChipsContainer>
                  {isMobile ? 
                    // 모바일에서는 지정된 순서대로 표시
                    mobileInquiryOrder.map((inquiry) => (
                      <Chip 
                        key={inquiry} 
                        active={selectedInquiries.includes(inquiry)}
                        onClick={() => handleInquirySelect(inquiry)}
                      >
                        <ChipText active={selectedInquiries.includes(inquiry)}>{inquiry}</ChipText>
                      </Chip>
                    ))
                    : 
                    // PC에서는 기존 순서대로 표시
                    inquiryTypes.map((inquiry) => (
                      <Chip 
                        key={inquiry} 
                        active={selectedInquiries.includes(inquiry)}
                        onClick={() => handleInquirySelect(inquiry)}
                      >
                        <ChipText active={selectedInquiries.includes(inquiry)}>{inquiry}</ChipText>
                      </Chip>
                    ))
                  }
                </ChipsContainer>
                <InputRowContainer>
                  <InputColumn>
                    <FormLabel>
                      <LabelText>기간</LabelText>
                      <RequiredMark>*</RequiredMark>
                    </FormLabel>
                    <TextField>
                      <InputField
                        type="number"
                        placeholder="프로젝트 예상 진행기간을 입력해 주세요."
                        value={duration}
                        onChange={handleDurationChange}
                        maxLength={20}
                        min="1"
                        pattern="[0-9]*"
                        inputMode="numeric"
                      />
                      <UnitText>개월</UnitText>
                    </TextField>
                  </InputColumn>
                  <InputColumn>
                    <FormLabel>
                      <LabelText>예산</LabelText>
                      <RequiredMark>*</RequiredMark>
                    </FormLabel>
                    <DropdownField
                      ref={budgetDropdownRef}
                      onClick={() => setBudgetDropdownOpen((open) => !open)}
                      style={{ position: "relative" }}
                    >
                      <div
                        style={{
                          flex: 1,
                          color: budget ? "#1D1D1D" : "#DCDCDC",
                          fontSize: 16,
                          fontFamily: 'Pretendard, sans-serif',
                          fontWeight: 500,
                          lineHeight: '28px',
                        }}
                      >
                        {budget || "예산을 선택해주세요"}
                      </div>
                      <ArrowIconWrapper>
                        <img src="/images/contact/dropdown.svg" alt="dropdown" />
                      </ArrowIconWrapper>
                      {budgetDropdownOpen && (
                        <CustomBudgetDropdown>
                          {budgetOptions.map((option) => (
                            <CustomBudgetOption
                              key={option}
                              onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                                e.stopPropagation();
                                setBudget(option);
                                setBudgetDropdownOpen(false);
                              }}
                            >
                              {option}
                            </CustomBudgetOption>
                          ))}
                        </CustomBudgetDropdown>
                      )}
                    </DropdownField>
                  </InputColumn>
                </InputRowContainer>
                
                {/* 문의 내용 입력 영역 추가 */}
                <TextAreaSection>
                  <FormLabel>
                    <LabelText>문의 내용</LabelText>
                    <RequiredMark>*</RequiredMark>
                  </FormLabel>
                  <TextAreaContainer>
                    <TextareaFrame>
                      <TextAreaInput 
                        placeholder="진행할 브랜드/서비스의 진행 상태(신규런칭, 리브랜딩, 이벤트 진행 등)와 마케팅 미션 및 고려 매체 등을 자세하게 기재해 주시면 정확한 답변을 드리는 데 도움이 됩니다."
                        value={description}
                        onChange={handleDescriptionChange}
                        maxLength={1000}
                      />
                    </TextareaFrame>
                    
                    {/* 파일 첨부 영역 */}
                    {files.length > 0 && (
                      <div style={{ width: '100%', margin: '0' }}>
                        {files.map((file, index) => {
                          const extension = file.name.split('.').pop()?.toUpperCase() || '';
                          const supportedTypes = ['CSV', 'DOC', 'DOCX', 'JPG', 'PDF', 'PNG', 'PPT', 'XLS'];
                          const iconPath = supportedTypes.includes(extension) 
                            ? `/images/contact/file-type/${extension}.svg` 
                            : '/images/contact/file-type/PDF.svg';
                          
                          return (
                            <AddedFileContainer key={index}>
                              <FileIcon>
                                <img 
                                  src={iconPath}
                                  alt={`${extension} file`}
                                  width="32"
                                  height="32"
                                />
                              </FileIcon>
                              <FileTextContent>
                                <FileName title={file.name}>{file.name}</FileName>
                                <FileSize>{formatFileSize(file.size)}</FileSize>
                              </FileTextContent>
                              <DeleteIconWrapper onClick={() => handleFileRemove(index)}>
                                <img 
                                  src="/images/icon/close.svg"
                                  alt="Delete file"
                                  width="16"
                                  height="16"
                                />
                              </DeleteIconWrapper>
                            </AddedFileContainer>
                          );
                        })}
                      </div>
                    )}
                    
                    <FileAddSection>
                      <FileButton
                        onClick={handleFileUpload}
                        disabled={files.length >= 2}
                      >
                        <FileButtonText>파일첨부</FileButtonText>
                      </FileButton>
                      <CaptionContainer>
                        <IconWrapper>
                          <img 
                            src="/images/icon/info.svg"
                            alt="Info"
                            width="16"
                            height="16"
                          />
                        </IconWrapper>
                        <CaptionText>
                          RFP, 참고문서 등 2개까지 업로드 가능 (최대 50MB) pdf, ppt, word, excel, hwp, jpg, png
                        </CaptionText>
                      </CaptionContainer>
                    </FileAddSection>
                  </TextAreaContainer>
                </TextAreaSection>

                {/* Replace MobileFixedButtonContainer with MobileButtonContainer */}
                {isMobile && (
                  <MobileButtonContainer>
                    <SubmitButton 
                      onClick={handleNextStep}
                      style={{ width: '100%', margin: 0 }}
                    >
                      <ButtonContentWrapper>
                        {isLoading && <LoadingSpinner />}
                        <span>다음</span>
                      </ButtonContentWrapper>
                    </SubmitButton>
                  </MobileButtonContainer>
                )}
              </FormSection>
            </ContentWrapper>
          ) : mobileStep === 2 ? (
            <ContentWrapper style={{ width: '100%', maxWidth: '710px', margin: '0 auto', paddingTop: 0 }}>
              <RightContentTitle style={{ marginTop: 48, marginBottom: 40 }}>의뢰인 정보를 알려주세요.</RightContentTitle>
              <FormSection style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 'calc(100vh - 210px)' }}>
                <div>
                  <ClientInfoRow style={{ marginBottom: isMobile ? '32px' : '20px' }}>
                    <InputColumn>
                      <FormLabel>
                        <LabelText>회사명</LabelText>
                        <RequiredMark>*</RequiredMark>
                      </FormLabel>
                      <TextField>
                        <InputField
                          type="text"
                          placeholder="의뢰하고자 하는 회사명/브랜드명을 입력해 주세요."
                          value={companyName}
                          onChange={handleCompanyNameChange}
                          maxLength={26}
                        />
                      </TextField>
                    </InputColumn>
                    <InputColumn>
                      <FormLabel>
                        <LabelText>담당자 이름</LabelText>
                        <RequiredMark>*</RequiredMark>
                      </FormLabel>
                      <TextField>
                        <InputField
                          type="text"
                          placeholder="프로젝트 담당자의 이름을 입력해 주세요."
                          value={name}
                          onChange={handleNameChange}
                          maxLength={26}
                        />
                      </TextField>
                    </InputColumn>
                  </ClientInfoRow>
                  <ClientInfoRow>
                    <InputColumn>
                      <FormLabel>
                        <LabelText>이메일 주소</LabelText>
                        <RequiredMark>*</RequiredMark>
                      </FormLabel>
                      <TextField>
                        <InputField
                          type="email"
                          placeholder="관련 자료를 받을 이메일 주소를 입력해 주세요."
                          value={email}
                          onChange={handleEmailChange}
                          style={{ 
                            borderColor: email && !isEmailValid ? '#FF4F00' : undefined,
                            color: isEmailValid ? '#1D1D1D' : undefined 
                          }}
                        />
                      </TextField>
                    </InputColumn>
                    <InputColumn>
                      <FormLabel>
                        <LabelText>연락처</LabelText>
                        <RequiredMark>*</RequiredMark>
                      </FormLabel>
                      <TextField>
                        <InputField
                          type="tel"
                          placeholder="프로젝트 담당자의 연락처를 입력해 주세요."
                          value={phone}
                          onChange={handlePhoneChange}
                          style={{ 
                            borderColor: phone && !isPhoneValid ? '#FF4F00' : undefined,
                            color: isPhoneValid ? '#1D1D1D' : undefined 
                          }}
                        />
                      </TextField>
                    </InputColumn>
                  </ClientInfoRow>
                </div>
                
                {/* Replace MobileFixedButtonContainer with MobileButtonContainer */}
                {isMobile && (
                  <MobileButtonContainer>
                    <SubmitButton 
                      onClick={handleMobileSubmit}
                      style={{ width: '100%', margin: 0 }}
                    >
                      <ButtonContentWrapper>
                        {isLoading && <LoadingSpinner />}
                        <span>제출하기</span>
                      </ButtonContentWrapper>
                    </SubmitButton>
                  </MobileButtonContainer>
                )}
              </FormSection>
            </ContentWrapper>
          ) : (
            <ContentWrapper style={{ width: '100%', maxWidth: '710px', margin: '0 auto', paddingTop: 0 }}>
              <RightContentTitle>의뢰인 정보를 알려주세요.</RightContentTitle>
              {/* 의뢰인 정보 입력 영역만 */}
              <FormSection>
                <ClientInfoRow style={{ marginBottom: isMobile ? '32px' : '20px' }}>
                  <InputColumn>
                    <FormLabel>
                      <LabelText>회사명</LabelText>
                      <RequiredMark>*</RequiredMark>
                    </FormLabel>
                    <TextField>
                      <InputField
                        type="text"
                        placeholder="의뢰하고자 하는 회사명/브랜드명을 입력해 주세요."
                        value={companyName}
                        onChange={handleCompanyNameChange}
                        maxLength={26}
                      />
                    </TextField>
                  </InputColumn>
                  
                  <InputColumn>
                    <FormLabel>
                      <LabelText>담당자 이름</LabelText>
                      <RequiredMark>*</RequiredMark>
                    </FormLabel>
                    <TextField>
                      <InputField
                        type="text"
                        placeholder="프로젝트 담당자의 이름을 입력해 주세요."
                        value={name}
                        onChange={handleNameChange}
                        maxLength={26}
                      />
                    </TextField>
                  </InputColumn>
                </ClientInfoRow>
                
                {/* 이메일 주소, 연락처 */}
                <ClientInfoRow>
                  <InputColumn>
                    <FormLabel>
                      <LabelText>이메일 주소</LabelText>
                      <RequiredMark>*</RequiredMark>
                    </FormLabel>
                    <TextField>
                      <InputField
                        type="email"
                        placeholder="관련 자료를 받을 이메일 주소를 입력해 주세요."
                        value={email}
                        onChange={handleEmailChange}
                        style={{ 
                          borderColor: email && !isEmailValid ? '#FF4F00' : undefined,
                          color: isEmailValid ? '#1D1D1D' : undefined 
                        }}
                      />
                    </TextField>
                  </InputColumn>
                  <InputColumn>
                    <FormLabel>
                      <LabelText>연락처</LabelText>
                      <RequiredMark>*</RequiredMark>
                    </FormLabel>
                    <TextField>
                      <InputField
                        type="tel"
                        placeholder="프로젝트 담당자의 연락처를 입력해 주세요."
                        value={phone}
                        onChange={handlePhoneChange}
                        style={{ 
                          borderColor: phone && !isPhoneValid ? '#FF4F00' : undefined,
                          color: isPhoneValid ? '#1D1D1D' : undefined 
                        }}
                      />
                    </TextField>
                  </InputColumn>
                </ClientInfoRow>
              </FormSection>
            </ContentWrapper>
          )}
          
          {/* Bottom Sheet Modal for mobile */}
          <BottomSheetOverlay 
            show={showBottomSheet} 
            onClick={() => setShowBottomSheet(false)}
          />
          <BottomSheetContainer 
            show={showBottomSheet} 
            animate={animateBottomSheet}
          >
            {!showPrivacyContent && (
              <>
                <AgreementItem 
                  style={{
                    width: '100%',
                    height: 48,
                    borderRadius: 12,
                    padding: 10,
                    backgroundColor: '#F8F8F8',
                    marginBottom: 20,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    boxSizing: 'border-box'
                  }}
                >
                  <Checkbox 
                    checked={bottomSheetPrivacyAgreed && bottomSheetNewsletterAgreed}
                    onClick={() => {
                      const newState = !(bottomSheetPrivacyAgreed && bottomSheetNewsletterAgreed);
                      setBottomSheetPrivacyAgreed(newState);
                      setBottomSheetNewsletterAgreed(newState);
                    }}
                  >
                    {(bottomSheetPrivacyAgreed && bottomSheetNewsletterAgreed) ? (
                      <img 
                        src="/images/icon/check.svg"
                        alt="Checked"
                        width="16"
                        height="16"
                      />
                    ) : (
                      <img 
                        src="/images/icon/unchecked.svg"
                        alt="Unchecked"
                        width="16"
                        height="16"
                      />
                    )}
                  </Checkbox>
                  <AgreementText>
                    <NormalText style={{ fontWeight: '700' }}>전체 동의</NormalText>
                  </AgreementText>
                </AgreementItem>
                
                <AgreementItem 
                  onClick={handleOpenPrivacyPolicy}
                  style={{ cursor: 'pointer' }}
                >
                  <Checkbox 
                    checked={bottomSheetPrivacyAgreed}
                    onClick={(e) => {
                      e.stopPropagation();
                      setBottomSheetPrivacyAgreed(!bottomSheetPrivacyAgreed);
                    }}
                  >
                    {bottomSheetPrivacyAgreed ? (
                      <img 
                        src="/images/icon/check.svg"
                        alt="Checked"
                        width="16"
                        height="16"
                      />
                    ) : (
                      <img 
                        src="/images/icon/unchecked.svg"
                        alt="Unchecked"
                        width="16"
                        height="16"
                      />
                    )}
                  </Checkbox>
                  <AgreementText>
                    <NormalText>개인정보 수집 및 이용에 동의 (필수)</NormalText>
                  </AgreementText>
                  <ArrowRight>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 6L15 12L9 18" stroke="#1D1D1D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </ArrowRight>
                </AgreementItem>
                
                <AgreementItem>
                  <Checkbox 
                    checked={bottomSheetNewsletterAgreed}
                    onClick={() => setBottomSheetNewsletterAgreed(!bottomSheetNewsletterAgreed)}
                  >
                    {bottomSheetNewsletterAgreed ? (
                      <img 
                        src="/images/icon/check.svg"
                        alt="Checked"
                        width="16"
                        height="16"
                      />
                    ) : (
                      <img 
                        src="/images/icon/unchecked.svg"
                        alt="Unchecked"
                        width="16"
                        height="16"
                      />
                    )}
                  </Checkbox>
                  <AgreementText>
                    <NormalText>뉴스레터 구독에 동의 (선택)</NormalText>
                  </AgreementText>
                </AgreementItem>
                
                <ConfirmButton 
                  onClick={handleBottomSheetConfirm}
                  disabled={!bottomSheetPrivacyAgreed || isLoading}
                  style={{ marginTop: 30 }}
                >
                  <ButtonContentWrapper>
                    {isLoading && <LoadingSpinner />}
                    <span>확인</span>
                  </ButtonContentWrapper>
                </ConfirmButton>
              </>
            )}
          </BottomSheetContainer>
          
          {/* Full page privacy policy view */}
          {showPrivacyContent && <PrivacyPolicyPage onBack={handlePrivacyPolicyBack} />}
        </>
      ) : (
        // Desktop view with left and right sections
        <>
          <AnimatedLeftSection>
            <ScrollingLeftContent>
              <LeftFrame ref={leftContentRef}>
                <ContactUsHeading>Contact us</ContactUsHeading>
                <TitleSection>
                  <SubTitle>킥오프 미팅만 하더라도<br/>다양한 인사이트를 얻을 수 있습니다.</SubTitle>
                  <Description>PUZL은 단발성이 아닌 장기적으로 함께 성장할 수 있는<br/>파트너를 찾습니다. 프로젝트 의뢰서(RFP)나 파트너 문의를<br/>주시면 최대한 빠르게 연락드리겠습니다.</Description>
                </TitleSection>
                
                {/* 데스크톱에서만 보이는 버튼 그룹 */}
                <ButtonGroup>
                  <Button onClick={handleDownloadClick}>
                    <ButtonText>회사소개서 다운로드</ButtonText>
                  </Button>
                  <Button onClick={handleMapViewClick}>
                    <ButtonText>지도보기</ButtonText>
                  </Button>
                </ButtonGroup>
                
                {/* 하단 주소 및 연락처 정보 */}
                <InfoSection style={{ marginTop: '40px' }}>
                  <MapIcon>
                    <img 
                      src="/images/icon/map.svg"
                      alt="Map"
                      width="36"
                      height="36"
                    />
                  </MapIcon>
                  <ContactInfo>
                    <AddressText>서울 마포구 양화로 81, L104</AddressText>
                    <EmailText style={{ textAlign: 'left', marginBottom: 20 }}>contact@puzl.co.kr</EmailText>
                    
                    {/* 모바일에서만 보이는 버튼 그룹 수정 */}
                    <MobileButtonGroup>
                      {/* 회사소개서와 지도보기 버튼을 가로로 배치 */}
                      <MobileButtonRow>
                        <MobileButton onClick={handleDownloadClick}>
                          <ButtonText>회사소개서 다운로드</ButtonText>
                        </MobileButton>
                        <MobileButton onClick={handleMapViewClick}>
                          <ButtonText>지도보기</ButtonText>
                        </MobileButton>
                      </MobileButtonRow>
                      
                      {/* 문의하기 버튼 추가 - 클릭 핸들러 수정 */}
                      <MobileInquiryButton onClick={handleMobileInquiryClick}>
                        문의하기
                      </MobileInquiryButton>
                    </MobileButtonGroup>
                  </ContactInfo>
                </InfoSection>
              </LeftFrame>
            </ScrollingLeftContent>
          </AnimatedLeftSection>
          
          <AnimatedRightSection>
            {/* 모바일에서는 폼이 보이거나 숨겨짐 */}
            <ContentWrapper id="mobile-contact-form">
              <RightContentTitle>문의하고 싶은 내용을 알려주세요.</RightContentTitle>
              
              <FormSection>
                <FormLabel>
                  <LabelText>문의 종류</LabelText>
                  <RequiredMark>*</RequiredMark>
                </FormLabel>
                
                {/* 문의 종류 칩(태그) - 단일 선택으로 수정 */}
                <ChipsContainer>
                  {isMobile ? 
                    // 모바일에서는 지정된 순서대로 표시
                    mobileInquiryOrder.map((inquiry) => (
                      <Chip 
                        key={inquiry} 
                        active={selectedInquiries.includes(inquiry)}
                        onClick={() => handleInquirySelect(inquiry)}
                      >
                        <ChipText active={selectedInquiries.includes(inquiry)}>{inquiry}</ChipText>
                      </Chip>
                    ))
                    : 
                    // PC에서는 기존 순서대로 표시
                    inquiryTypes.map((inquiry) => (
                      <Chip 
                        key={inquiry} 
                        active={selectedInquiries.includes(inquiry)}
                        onClick={() => handleInquirySelect(inquiry)}
                      >
                        <ChipText active={selectedInquiries.includes(inquiry)}>{inquiry}</ChipText>
                      </Chip>
                    ))
                  }
                </ChipsContainer>
                <InputRowContainer>
                  <InputColumn>
                    <FormLabel>
                      <LabelText>기간</LabelText>
                      <RequiredMark>*</RequiredMark>
                    </FormLabel>
                    <TextField>
                      <InputField
                        type="number"
                        placeholder="프로젝트 예상 진행기간을 입력해 주세요."
                        value={duration}
                        onChange={handleDurationChange}
                        maxLength={20}
                        min="1"
                        pattern="[0-9]*"
                        inputMode="numeric"
                      />
                      <UnitText>개월</UnitText>
                    </TextField>
                  </InputColumn>
                  <InputColumn>
                    <FormLabel>
                      <LabelText>예산</LabelText>
                      <RequiredMark>*</RequiredMark>
                    </FormLabel>
                    <DropdownField
                      ref={budgetDropdownRef}
                      onClick={() => setBudgetDropdownOpen((open) => !open)}
                      style={{ position: "relative" }}
                    >
                      <div
                        style={{
                          flex: 1,
                          color: budget ? "#1D1D1D" : "#DCDCDC",
                          fontSize: 16,
                          fontFamily: 'Pretendard, sans-serif',
                          fontWeight: 500,
                          lineHeight: '28px',
                        }}
                      >
                        {budget || "예산을 선택해주세요"}
                      </div>
                      <ArrowIconWrapper>
                        <img src="/images/contact/dropdown.svg" alt="dropdown" />
                      </ArrowIconWrapper>
                      {budgetDropdownOpen && (
                        <CustomBudgetDropdown>
                          {budgetOptions.map((option) => (
                            <CustomBudgetOption
                              key={option}
                              onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                                e.stopPropagation();
                                setBudget(option);
                                setBudgetDropdownOpen(false);
                              }}
                            >
                              {option}
                            </CustomBudgetOption>
                          ))}
                        </CustomBudgetDropdown>
                      )}
                    </DropdownField>
                  </InputColumn>
                </InputRowContainer>
                
                {/* 문의 내용 입력 영역 추가 */}
                <TextAreaSection>
                  <FormLabel>
                    <LabelText>문의 내용</LabelText>
                    <RequiredMark>*</RequiredMark>
                  </FormLabel>
                  <TextAreaContainer>
                    <TextareaFrame>
                      <TextAreaInput 
                        placeholder="진행할 브랜드/서비스의 진행 상태(신규런칭, 리브랜딩, 이벤트 진행 등)와 마케팅 미션 및 고려 매체 등을 자세하게 기재해 주시면 정확한 답변을 드리는 데 도움이 됩니다."
                        value={description}
                        onChange={handleDescriptionChange}
                        maxLength={1000}
                      />
                    </TextareaFrame>
                    
                    {/* 파일 첨부 영역 */}
                    {files.length > 0 && (
                      <div style={{ width: '100%', margin: '0' }}>
                        {files.map((file, index) => {
                          const extension = file.name.split('.').pop()?.toUpperCase() || '';
                          const supportedTypes = ['CSV', 'DOC', 'DOCX', 'JPG', 'PDF', 'PNG', 'PPT', 'XLS'];
                          const iconPath = supportedTypes.includes(extension) 
                            ? `/images/contact/file-type/${extension}.svg` 
                            : '/images/contact/file-type/PDF.svg';
                          
                          return (
                            <AddedFileContainer key={index}>
                              <FileIcon>
                                <img 
                                  src={iconPath}
                                  alt={`${extension} file`}
                                  width="32"
                                  height="32"
                                />
                              </FileIcon>
                              <FileTextContent>
                                <FileName title={file.name}>{file.name}</FileName>
                                <FileSize>{formatFileSize(file.size)}</FileSize>
                              </FileTextContent>
                              <DeleteIconWrapper onClick={() => handleFileRemove(index)}>
                                <img 
                                  src="/images/icon/close.svg"
                                  alt="Delete file"
                                  width="16"
                                  height="16"
                                />
                              </DeleteIconWrapper>
                            </AddedFileContainer>
                          );
                        })}
                      </div>
                    )}
                    
                    <FileAddSection>
                      <FileButton
                        onClick={handleFileUpload}
                        disabled={files.length >= 2}
                      >
                        <FileButtonText>파일첨부</FileButtonText>
                      </FileButton>
                      <CaptionContainer>
                        <IconWrapper>
                          <img 
                            src="/images/icon/info.svg"
                            alt="Info"
                            width="16"
                            height="16"
                          />
                        </IconWrapper>
                        <CaptionText>
                          RFP, 참고문서 등 2개까지 업로드 가능 (최대 50MB) pdf, ppt, word, excel, hwp, jpg, png
                        </CaptionText>
                      </CaptionContainer>
                    </FileAddSection>
                  </TextAreaContainer>
                </TextAreaSection>

                {/* 모바일에서는 더 넓은 마진을 제공하여 하단 고정 버튼을 위한 공간 확보 */}
                {isMobile && <div style={{ marginBottom: '100px' }}></div>}
                
                {/* PC에서는 일반 버튼으로 표시 - width 200px, 텍스트 "문의하기"로 변경 */}
                {!isMobile && (
                  <>
                    {/* 의뢰인 정보 섹션 - PC에서만 표시 */}
                    <ClientInfoSection>
                      <ClientInfoTitle style={{ marginTop: '50px' }}>의뢰인 정보를 알려주세요.</ClientInfoTitle>
                      
                      {/* 회사명, 담당자 이름 */}
                      <ClientInfoRow>
                        <InputColumn>
                          <FormLabel>
                            <LabelText>회사명</LabelText>
                            <RequiredMark>*</RequiredMark>
                          </FormLabel>
                          <TextField>
                            <InputField
                              type="text"
                              placeholder="의뢰하고자 하는 회사명/브랜드명을 입력해 주세요."
                              value={companyName}
                              onChange={handleCompanyNameChange}
                              maxLength={26}
                            />
                          </TextField>
                        </InputColumn>
                        <InputColumn>
                          <FormLabel>
                            <LabelText>담당자 이름</LabelText>
                            <RequiredMark>*</RequiredMark>
                          </FormLabel>
                          <TextField>
                            <InputField
                              type="text"
                              placeholder="프로젝트 담당자의 이름을 입력해 주세요."
                              value={name}
                              onChange={handleNameChange}
                              maxLength={26}
                            />
                          </TextField>
                        </InputColumn>
                      </ClientInfoRow>
                      
                      {/* 이메일 주소, 연락처 */}
                      <ClientInfoRow>
                        <InputColumn>
                          <FormLabel>
                            <LabelText>이메일 주소</LabelText>
                            <RequiredMark>*</RequiredMark>
                          </FormLabel>
                          <TextField>
                            <InputField
                              type="email"
                              placeholder="관련 자료를 받을 이메일 주소를 입력해 주세요."
                              value={email}
                              onChange={handleEmailChange}
                              style={{ 
                                borderColor: email && !isEmailValid ? '#FF4F00' : undefined,
                                color: isEmailValid ? '#1D1D1D' : undefined 
                              }}
                            />
                          </TextField>
                        </InputColumn>
                        <InputColumn>
                          <FormLabel>
                            <LabelText>연락처</LabelText>
                            <RequiredMark>*</RequiredMark>
                          </FormLabel>
                          <TextField>
                            <InputField
                              type="tel"
                              placeholder="프로젝트 담당자의 연락처를 입력해 주세요."
                              value={phone}
                              onChange={handlePhoneChange}
                              style={{ 
                                borderColor: phone && !isPhoneValid ? '#FF4F00' : undefined,
                                color: isPhoneValid ? '#1D1D1D' : undefined 
                              }}
                            />
                          </TextField>
                        </InputColumn>
                      </ClientInfoRow>
                    </ClientInfoSection>
                    
                    {/* 동의 항목 수정 - PC에서만 표시 */}
                    <AgreementSection>
                      <AgreementRow>
                        {/* 개인정보 수집 및 이용 동의 */}
                        <CheckboxContainer onClick={handleAgreementToggle}>
                          <Checkbox 
                            checked={isAgreed}
                          >
                            {isAgreed ? (
                              <img 
                                src="/images/icon/check.svg"
                                alt="Checked"
                                width="16"
                                height="16"
                              />
                            ) : (
                              <img 
                                src="/images/icon/unchecked.svg"
                                alt="Unchecked"
                                width="16"
                                height="16"
                              />
                            )}
                          </Checkbox>
                          <AgreementText>
                            <HighlightText onClick={handleShowAgreementDetails}>개인정보 수집 및 이용</HighlightText>
                            <NormalText>에 동의합니다.</NormalText>
                          </AgreementText>
                        </CheckboxContainer>
                        
                        {/* 뉴스레터 구독 동의 */}
                        <CheckboxContainer onClick={handleNewsletterToggle}>
                          <Checkbox 
                            checked={isNewsletterAgreed}
                          >
                            {isNewsletterAgreed ? (
                              <img 
                                src="/images/icon/check.svg"
                                alt="Checked"
                                width="16"
                                height="16"
                              />
                            ) : (
                              <img 
                                src="/images/icon/unchecked.svg"
                                alt="Unchecked"
                                width="16"
                                height="16"
                              />
                            )}
                          </Checkbox>
                          <AgreementText>
                            <HighlightText>뉴스레터 구독</HighlightText>
                            <NormalText>에 동의합니다.</NormalText>
                          </AgreementText>
                        </CheckboxContainer>
                      </AgreementRow>
                      
                      {/* 문의하기 버튼 */}
                      <SubmitButtonContainer>
                        <SubmitButton 
                          style={{ width: '200px' }} 
                          onClick={handleSubmit}
                        >
                          <ButtonContentWrapper>
                            {isLoading && <LoadingSpinner />}
                            <span>문의하기</span>
                          </ButtonContentWrapper>
                        </SubmitButton>
                      </SubmitButtonContainer>
                    </AgreementSection>
                  </>
                )}
              </FormSection>
            </ContentWrapper>
          </AnimatedRightSection>
        </>
      )}
      <PrivacyPolicyPopup
        isOpen={isPrivacyPolicyOpen}
        onClose={() => setIsPrivacyPolicyOpen(false)}
        onConfirm={handlePrivacyPolicyConfirm}
        isFullPage={isMobile}
      />
    </Container>
  );
};

export default Contact;
